import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  AlertTriangle, ArrowRight, Check, ChevronDown, ChevronRight, Copy, Info, Loader2, RefreshCw, ShieldCheck, Users, X,
} from 'lucide-react';
import { apiService, type GroupNodeDTO, type ProjectGroupDTO } from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { isRanksOnlyGroup, type GameGroupLabel } from '@/utils/gameGroups';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { listGradesOn } from './gradesByGameGroup';
import {
  copyOutcome, danglingInheritances, defaultCopyTarget, defaultSelection, describeDefinition, describeTotals, describeUpdate,
  expiryOf, explainError, isSelectable, kindOf, mapLimit, normalizeContext, NODE_KINDS, plural, planGradeCopy, runGradeCopy,
  summarizeSelection, type GradeCopyApi, type GradeCopyEntry, type GradeCopyProgress, type GradeCopyResult, type GradeCopyStatus,
  type GradeSide,
} from './gradeCopy';
import styles from './GradeCopyDialog.module.scss';

/**
 * « Copier les grades vers… » (Supervision > Grades, jeu à groupes) : la définition des grades du
 * groupe affiché (source) devient identique dans un autre groupe du projet (cible), grade par grade.
 *
 * Étapes : comparaison des deux LuckPerms, aperçu (à créer, à mettre à jour, identique, seulement
 * dans la cible ; tout coché par défaut), confirmation qui récapitule, puis exécution avec barre
 * d'avancement et compte rendu par grade. Toutes les écritures visent la cible, par les points
 * d'entrée habituels du satellite : la passerelle exige le droit d'écrire les grades et
 * l'affectation au groupe cible, le satellite garde les grades protégés et RankNodeGuard. Les
 * grades des joueurs ne sont jamais lus ni écrits ; un grade qui n'existe que dans la cible n'est
 * jamais supprimé (logique et textes : gradeCopy.ts).
 */

/** Lectures de nodes lancées en même temps (une par grade et par groupe). */
const READ_PARALLELISM = 4;

type Step =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'unsupported'; groups: string[] }
  | { kind: 'preview' }
  | { kind: 'confirm' }
  | { kind: 'running' }
  | { kind: 'done' };

interface GradeCopyDialogProps {
  /** Groupe affiché : la source. */
  source: GameGroupLabel;
  /** Autres groupes utilisables (affectation, acceptés par la passerelle) : les cibles possibles. */
  targets: ProjectGroupDTO[];
  onClose: () => void;
}

/** « d'Iris », « de Nova ». */
function deGroup(label: string): string {
  return /^[aeiouyhàâéèêëîïôûü]/i.test(label) ? `d'${label}` : `de ${label}`;
}

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

function formatExpiry(sec: number): string {
  return new Date(sec * 1000).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const STATUS_LABEL: Record<GradeCopyStatus, (target: string) => string> = {
  create: () => 'à créer',
  update: () => 'à mettre à jour',
  same: () => 'identique',
  targetOnly: (t) => `seulement sur ${t}`,
};

const STATUS_CHIP: Record<GradeCopyStatus, string> = {
  create: styles.chipCreate,
  update: styles.chipUpdate,
  same: styles.chipSame,
  targetOnly: styles.chipTargetOnly,
};

const SECTIONS: { status: GradeCopyStatus; title: (target: string) => string }[] = [
  { status: 'create', title: () => 'À créer' },
  { status: 'update', title: () => 'À mettre à jour' },
  { status: 'same', title: () => 'Identiques' },
  { status: 'targetOnly', title: (t) => `Seulement sur ${t} (jamais supprimés)` },
];

export function GradeCopyDialog({ source, targets, onClose }: GradeCopyDialogProps) {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const gameId = useGameStore((s) => s.activeGameId);
  const setActiveGameGroup = useGameStore((s) => s.setActiveGameGroup);

  const [targetKey, setTargetKey] = useState<string>(() => defaultCopyTarget(targets, isRanksOnlyGroup)?.key ?? '');
  const target = targets.find((g) => g.key === targetKey) ?? null;
  const targetLabel = target?.label ?? targetKey;
  // La page recrée la liste des cibles à chaque rendu : la comparaison ne doit pas repartir pour autant.
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const [step, setStep] = useState<Step>(() => (targetKey
    ? { kind: 'loading' }
    : { kind: 'error', message: "Aucun autre groupe du projet ne t'est ouvert : rien vers quoi copier." }));
  const [entries, setEntries] = useState<GradeCopyEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<GradeCopyProgress | null>(null);
  const [results, setResults] = useState<GradeCopyResult[] | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  // Une comparaison plus récente (autre cible, Réessayer) remplace la précédente.
  const loadSeq = useRef(0);

  const running = step.kind === 'running';

  const load = useCallback(async (key: string) => {
    const seq = ++loadSeq.current;
    const label = targetsRef.current.find((g) => g.key === key)?.label ?? key;
    setStep({ kind: 'loading' });
    setResults(null);
    setProgress(null);
    const [src, tgt] = await Promise.all([listGradesOn(source.key), listGradesOn(key)]);
    if (seq !== loadSeq.current) return;
    if (!src.data) {
      setStep({ kind: 'error', message: `Grades ${deGroup(source.label)} illisibles : ${explainError(src.error)}.` });
      return;
    }
    if (!tgt.data) {
      setStep({ kind: 'error', message: `Grades ${deGroup(label)} illisibles : ${explainError(tgt.error)}.` });
      return;
    }
    const srcGrades = src.data.groups;
    const tgtGrades = tgt.data.groups;
    const inSource = new Set(srcGrades.map((g) => g.name.toLowerCase()));
    const common = tgtGrades.filter((g) => inSource.has(g.name.toLowerCase()));
    // Sans grade commun, un grade de la cible est lu quand même : sa réponse dit si son satellite
    // donne les contextes (sinon rien ne le dirait avant la première écriture).
    const targetReads = common.length > 0 ? common : tgtGrades.slice(0, 1);
    const jobs = [
      ...srcGrades.map((g) => ({ side: 'source' as const, name: g.name })),
      ...targetReads.map((g) => ({ side: 'target' as const, name: g.name })),
    ];
    const reads = await mapLimit(jobs, READ_PARALLELISM, (job) =>
      apiService.getGroupPermissions(job.name, { gameGroup: job.side === 'source' ? source.key : key }));
    if (seq !== loadSeq.current) return;

    const notDetailed = new Set<string>();
    reads.forEach((r, i) => {
      if (r.data && !r.data.detailed) notDetailed.add(jobs[i]!.side === 'source' ? source.label : label);
    });
    if (notDetailed.size > 0) {
      setStep({ kind: 'unsupported', groups: [...notDetailed] });
      return;
    }

    const readOf = (side: 'source' | 'target', name: string) => {
      const i = jobs.findIndex((j) => j.side === side && j.name === name);
      return i < 0 ? undefined : reads[i];
    };
    const sideOf = (side: 'source' | 'target', g: { name: string; weight?: number | null }): GradeSide => {
      const r = readOf(side, g.name);
      return {
        name: g.name,
        weight: g.weight ?? null,
        nodes: r?.data ? r.data.permissions : null,
        ...(r && !r.data ? { error: explainError(r.error) } : {}),
      };
    };
    const plan = planGradeCopy({
      source: srcGrades.map((g) => sideOf('source', g)),
      target: tgtGrades.map((g) => sideOf('target', g)),
      protectedGrades: (tgt.data.protectedGrades ?? []).map((p) => p.toLowerCase()),
      isAdmin,
      sourceLabel: source.label,
      targetLabel: label,
      nowSec: nowSec(),
    });
    setEntries(plan);
    setSelected(defaultSelection(plan));
    setExpanded(new Set());
    setStep({ kind: 'preview' });
  }, [source.key, source.label, isAdmin]);

  useEffect(() => {
    if (targetKey) void load(targetKey);
  }, [targetKey, load]);

  // Fermeture (Échap, croix, clic à côté) : jamais pendant la copie.
  const close = useCallback(() => {
    if (!running) onClose();
  }, [running, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const summary = useMemo(() => summarizeSelection(entries, selected), [entries, selected]);
  const selectableNames = useMemo(() => entries.filter(isSelectable).map((e) => e.name), [entries]);
  const now = nowSec();

  const toggle = (name: string) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const toggleExpanded = (name: string) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const run = async () => {
    if (!target) return;
    const chosen = entries.filter((e) => isSelectable(e) && selected.has(e.name));
    const opts = { gameGroup: target.key };
    const api: GradeCopyApi = {
      createGrade: (name) => apiService.createGroup(name, opts),
      readNodes: (name) => apiService.getGroupPermissions(name, opts),
      addNode: (name, node) => apiService.addGroupNode(name, node, opts),
      removeNode: (name, node) => apiService.removeGroupNode(name, node, opts),
    };
    setStep({ kind: 'running' });
    setRunError(null);
    setProgress({ done: 0, total: 1, current: null, currentApplied: 0, currentTotal: 0, results: [] });
    let finalProgress: GradeCopyProgress | null = null;
    try {
      const out = await runGradeCopy(chosen, api, {
        targetLabel: target.label,
        now: nowSec,
        onProgress: (p) => { finalProgress = p; setProgress(p); },
      });
      setResults(out);
    } catch (e) {
      // Ne devrait pas arriver (les appels rendent leurs erreurs) : la fenêtre ne reste jamais bloquée.
      setRunError(e instanceof Error ? e.message : String(e));
      setResults((finalProgress as GradeCopyProgress | null)?.results ?? []);
    } finally {
      setStep({ kind: 'done' });
    }
  };

  const showTarget = () => {
    if (!target) return;
    setActiveGameGroup(gameId, target.key);
    onClose();
  };

  // Héritages qui ne mèneront à rien, sur les grades cochés.
  const danglingOf = (e: GradeCopyEntry) =>
    (selected.has(e.name) && isSelectable(e) ? danglingInheritances(e, entries, selected, now) : []);
  const danglingAll = entries.flatMap((e) => danglingOf(e).map((g) => `${e.name} hérite de ${g}`));

  const counts = {
    create: entries.filter((e) => e.status === 'create').length,
    update: entries.filter((e) => e.status === 'update').length,
    same: entries.filter((e) => e.status === 'same').length,
    targetOnly: entries.filter((e) => e.status === 'targetOnly').length,
  };
  const sourceCount = counts.create + counts.update + counts.same;
  const blockedCount = entries.filter((e) => e.blocked).length;
  const chosenCount = summary.create.length + summary.update.length;

  let body;
  let footer;
  if (step.kind === 'loading') {
    body = (
      <div className={styles.loading}>
        <Loader2 size={18} className={styles.spin} />
        <span>Comparaison des grades {deGroup(source.label)} et {deGroup(targetLabel)}…</span>
      </div>
    );
    footer = <button type="button" className={styles.ghostBtn} onClick={close}>Annuler</button>;
  } else if (step.kind === 'error' || step.kind === 'unsupported') {
    body = step.kind === 'error' ? (
      <div className={`${styles.notice} ${styles.noticeError}`}><AlertTriangle size={16} /> <span>{step.message}</span></div>
    ) : (
      <div className={`${styles.notice} ${styles.noticeWarning}`}>
        <AlertTriangle size={16} />
        <span>
          {step.groups.length === 1
            ? `Le satellite ${deGroup(step.groups[0]!)} ne donne pas encore`
            : `Les satellites ${step.groups.map(deGroup).join(' et ')} ne donnent pas encore`} les contextes ni les fins des
          permissions. Sans eux, une permission limitée à un serveur serait copiée pour tous les serveurs : la copie attend
          la mise à jour de BE-Panel-Games sur {listOf(step.groups)}. Rien n'a été écrit.
        </span>
      </div>
    );
    footer = (
      <>
        <button type="button" className={styles.ghostBtn} onClick={close}>Fermer</button>
        <button type="button" className={styles.secondaryBtn} onClick={() => void load(targetKey)}>
          <RefreshCw size={14} /> Réessayer
        </button>
      </>
    );
  } else if (step.kind === 'preview') {
    body = (
      <>
        <ScopeNotice source={source.label} target={targetLabel} />
        {blockedCount > 0 && !isAdmin && (
          <div className={`${styles.notice} ${styles.noticeInfo}`}>
            <ShieldCheck size={16} />
            <span>
              Grades protégés et nodes réservés aux admins (« * », LuckPerms, héritage d'un grade protégé) : leur copie
              est réservée aux admins du panel, ils restent décochés.
            </span>
          </div>
        )}
        <div className={styles.toolbar}>
          <span>
            {plural(sourceCount, 'grade', 'grades')} sur {source.label} : {counts.create} à créer, {counts.update} à mettre à jour,{' '}
            {plural(counts.same, 'identique', 'identiques')}
            {counts.targetOnly > 0 && <> · {counts.targetOnly} seulement sur {targetLabel}</>}
          </span>
          {selectableNames.length > 0 && (
            <span className={styles.toolbarActions}>
              <button type="button" className={styles.linkBtn} onClick={() => setSelected(new Set(selectableNames))}
                disabled={selected.size === selectableNames.length}>Tout cocher</button>
              <button type="button" className={styles.linkBtn} onClick={() => setSelected(new Set())}
                disabled={selected.size === 0}>Tout décocher</button>
            </span>
          )}
        </div>
        {selectableNames.length === 0 && counts.create + counts.update === 0 && (
          <div className={`${styles.notice} ${styles.noticeSuccess}`}>
            <Check size={16} /> <span>Rien à copier : les grades {deGroup(source.label)} sont déjà identiques sur {targetLabel}.</span>
          </div>
        )}
        {SECTIONS.map(({ status, title }) => {
          const list = entries.filter((e) => e.status === status);
          if (list.length === 0) return null;
          return (
            <section key={status} className={styles.section} aria-label={title(targetLabel)}>
              <div className={styles.sectionTitle}>{title(targetLabel)} ({list.length})</div>
              <ul className={styles.grades}>
                {list.map((e) => (
                  <GradeRow
                    key={e.name}
                    entry={e}
                    targetLabel={targetLabel}
                    checked={selected.has(e.name)}
                    open={expanded.has(e.name)}
                    dangling={danglingOf(e)}
                    nowSec={now}
                    onToggle={() => toggle(e.name)}
                    onExpand={() => toggleExpanded(e.name)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </>
    );
    footer = (
      <>
        <span className={styles.footerNote}>Aucune écriture avant la confirmation.</span>
        <button type="button" className={styles.ghostBtn} onClick={close}>Annuler</button>
        <button type="button" className={styles.primaryBtn} disabled={chosenCount === 0} onClick={() => setStep({ kind: 'confirm' })}>
          Continuer avec {plural(chosenCount, 'grade', 'grades')} <ArrowRight size={14} />
        </button>
      </>
    );
  } else if (step.kind === 'confirm') {
    body = (
      <div className={styles.recap}>
        <p>
          Les grades cochés {deGroup(source.label)} vont remplacer leur définition sur {targetLabel}. Vérifie avant de
          lancer la copie :
        </p>
        <div className={styles.recapBox}>
          <span className={styles.recapTitle}>Ce qui change sur {targetLabel}</span>
          <ul>
            {summary.create.length > 0 && (
              <li>{plural(summary.create.length, 'grade créé', 'grades créés')} : {summary.create.join(', ')}</li>
            )}
            {summary.update.length > 0 && (
              <li>{plural(summary.update.length, 'grade mis à jour', 'grades mis à jour')} : {summary.update.join(', ')}</li>
            )}
            {describeTotals(summary.counts).map((line) => <li key={line}>{line}</li>)}
            <li>{plural(summary.operations, 'écriture', 'écritures')} dans le LuckPerms {deGroup(targetLabel)}</li>
          </ul>
        </div>
        <div className={styles.recapBox}>
          <span className={styles.recapTitle}>Ce qui ne change pas</span>
          <ul>
            <li>Aucun joueur ne gagne ni ne perd un grade, ni sur {source.label} ni sur {targetLabel}.</li>
            {summary.targetOnly > 0 && (
              <li>
                {summary.targetOnly === 1
                  ? `1 grade qui n'existe que sur ${targetLabel} reste tel quel.`
                  : `${summary.targetOnly} grades qui n'existent que sur ${targetLabel} restent tels quels.`}
              </li>
            )}
            {summary.unchecked > 0 && (
              <li>
                {summary.unchecked === 1
                  ? `1 grade décoché reste comme avant sur ${targetLabel}.`
                  : `${summary.unchecked} grades décochés restent comme avant sur ${targetLabel}.`}
              </li>
            )}
            <li>Les grades {deGroup(source.label)} ne sont pas modifiés.</li>
          </ul>
        </div>
        {danglingAll.length > 0 && (
          <div className={`${styles.notice} ${styles.noticeWarning}`}>
            <AlertTriangle size={16} />
            <span>
              Héritages vers un grade qui n'existera pas sur {targetLabel} : {danglingAll.join(', ')}. Coche aussi ces
              grades pour qu'ils soient créés.
            </span>
          </div>
        )}
      </div>
    );
    footer = (
      <>
        <button type="button" className={styles.ghostBtn} onClick={() => setStep({ kind: 'preview' })}>Retour</button>
        <button type="button" className={styles.primaryBtn} onClick={() => void run()}>
          <Copy size={14} /> Copier vers {targetLabel}
        </button>
      </>
    );
  } else {
    const p = progress;
    const pct = p && p.total > 0 ? Math.min(100, Math.round((p.done / p.total) * 100)) : 0;
    const outcome = results ? copyOutcome(results, targetLabel) : null;
    const shown = results ?? p?.results ?? [];
    body = (
      <>
        {runError ? (
          <div className={`${styles.notice} ${styles.noticeError}`}>
            <AlertTriangle size={16} />
            <span>
              Copie interrompue ({runError}). Les grades ci-dessous ont été traités ; compare à nouveau pour voir où en
              est {targetLabel}.
            </span>
          </div>
        ) : outcome ? (
          <div className={`${styles.notice} ${outcome.level === 'success' ? styles.noticeSuccess : outcome.level === 'warning' ? styles.noticeWarning : styles.noticeError}`}>
            {outcome.level === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
            <span>{outcome.message}</span>
          </div>
        ) : (
          <div className={styles.progress}>
            <div className={styles.progressBar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
              aria-label={`Copie vers ${targetLabel}`}>
              <div className={styles.progressFill} style={{ width: `${pct}%` }} />
            </div>
            <span className={styles.progressText}>
              {pct} %
              {p?.current ? (
                <> · grade « {p.current} »{p.currentTotal > 0 && <> : {p.currentApplied} {p.currentApplied > 1 ? 'écritures' : 'écriture'} sur {p.currentTotal}</>}</>
              ) : null}
            </span>
          </div>
        )}
        {shown.length > 0 && (
          <ul className={styles.results} aria-label="Compte rendu par grade">
            {shown.map((r) => <ResultRow key={r.name} result={r} />)}
          </ul>
        )}
      </>
    );
    footer = step.kind === 'running' ? (
      <span className={styles.footerNote}>
        <Loader2 size={12} className={styles.spin} /> Copie en cours : garde cette fenêtre ouverte jusqu'à la fin.
      </span>
    ) : (
      <>
        <button type="button" className={styles.ghostBtn} onClick={() => void load(targetKey)}>
          <RefreshCw size={14} /> Comparer à nouveau
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={showTarget}>Voir les grades {deGroup(targetLabel)}</button>
        <button type="button" className={styles.primaryBtn} onClick={close}>Fermer</button>
      </>
    );
  }

  const titleId = 'grade-copy-title';
  const selectLocked = step.kind === 'running' || step.kind === 'confirm';

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={target?.color ? { '--copy-color': target.color } as CSSProperties : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <h2 id={titleId} className={styles.title}>
              <Copy size={20} /> Copier les grades {deGroup(source.label)}
            </h2>
            <button type="button" className={styles.close} onClick={close} disabled={running}
              aria-label="Fermer" title={running ? 'Copie en cours' : 'Fermer'}>
              <X size={18} />
            </button>
          </div>
          <div className={styles.route}>
            <GroupBadge group={source} size="md" title={`Source : ${source.label}`} />
            <ArrowRight size={16} aria-hidden />
            <span>vers</span>
            <select className={styles.select} value={targetKey} disabled={selectLocked || targets.length === 0}
              onChange={(e) => setTargetKey(e.target.value)} aria-label="Groupe cible">
              {targets.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            {target && <GroupBadge group={target} size="md" title={`Cible : ${target.label}`} />}
          </div>
        </header>
        <div className={styles.body}>{body}</div>
        <footer className={styles.footer}>{footer}</footer>
      </div>
    </div>
  );
}

function listOf(xs: readonly string[]): string {
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;
}

/** Ce qui est copié, ce qui ne l'est pas : affiché en tête de l'aperçu. */
function ScopeNotice({ source, target }: { source: string; target: string }) {
  return (
    <div className={styles.scope}>
      <div className={styles.scopeLine}>
        <Copy size={14} />
        <span>
          <strong>Copié</strong> : la définition de chaque grade coché (permissions avec leur contexte et leur fin, héritages,
          poids, préfixe, suffixe, nom affiché, métas). Sur {target}, il devient identique à celui {deGroup(source)}.
        </span>
      </div>
      <div className={styles.scopeLine}>
        <Users size={14} />
        <span><strong>Pas copié</strong> : qui a le grade. Aucun joueur ne gagne ni ne perd un grade, ni sur {source} ni sur {target}.</span>
      </div>
      <div className={styles.scopeLine}>
        <ShieldCheck size={14} />
        <span><strong>Jamais supprimé</strong> : un grade qui n'existe que sur {target} reste tel quel.</span>
      </div>
      <div className={styles.scopeLine}>
        <Info size={14} />
        <span>Cosmétiques, chat et boutique sont communs à tout le projet : rien à copier.</span>
      </div>
    </div>
  );
}

interface GradeRowProps {
  entry: GradeCopyEntry;
  targetLabel: string;
  checked: boolean;
  open: boolean;
  dangling: string[];
  nowSec: number;
  onToggle: () => void;
  onExpand: () => void;
}

function GradeRow({ entry: e, targetLabel, checked, open, dangling, nowSec, onToggle, onExpand }: GradeRowProps) {
  const selectable = isSelectable(e);
  const hasDetail = e.status === 'create' || e.status === 'update';
  let info: string[] = [];
  if (e.status === 'create') info = [describeDefinition(e.source, nowSec).join(' · ')];
  else if (e.status === 'update') info = describeUpdate(e.changes);
  else if (e.status === 'same') info = [`Déjà identique sur ${targetLabel} : rien à écrire.`];
  else info = [`N'existe que sur ${targetLabel} : gardé tel quel.`];

  return (
    <li className={`${styles.grade} ${selectable && checked ? styles.gradeChecked : ''}`}>
      <div className={styles.gradeLine}>
        <label className={`${styles.check} ${selectable ? '' : styles.checkOff}`}>
          {selectable ? (
            <input type="checkbox" checked={checked} onChange={onToggle} aria-label={`Copier le grade ${e.name}`} />
          ) : (
            <span className={styles.checkSpacer} />
          )}
          <span className={styles.gradeName}>{e.name}</span>
        </label>
        {e.weight != null && <span className={styles.weight} title={`Poids LuckPerms : ${e.weight}`}>{e.weight}</span>}
        <span className={`${styles.chip} ${STATUS_CHIP[e.status]}`}>{STATUS_LABEL[e.status](targetLabel)}</span>
        {e.blocked && <span className={`${styles.chip} ${styles.chipBlocked}`}><ShieldCheck size={11} /> bloqué</span>}
        {hasDetail && (
          <button type="button" className={styles.expand} onClick={onExpand} aria-expanded={open}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Détail
          </button>
        )}
      </div>
      <div className={styles.gradeInfo}>
        {info.map((line) => <span key={line}>{line}</span>)}
        {e.blocked && <span className={styles.blockedText}>{e.blocked}</span>}
        {dangling.length > 0 && (
          <span className={styles.warnText}>
            Hérite de {listOf(dangling)}, absent de {targetLabel} et pas coché : cet héritage ne mènera à rien.
          </span>
        )}
      </div>
      {open && hasDetail && <NodesDetail entry={e} nowSec={nowSec} />}
    </li>
  );
}

function sortNodes(nodes: readonly GroupNodeDTO[]): GroupNodeDTO[] {
  return [...nodes].sort((a, b) => NODE_KINDS.indexOf(kindOf(a.key)) - NODE_KINDS.indexOf(kindOf(b.key)) || a.key.localeCompare(b.key));
}

function NodesDetail({ entry: e, nowSec }: { entry: GradeCopyEntry; nowSec: number }) {
  if (e.status === 'create') {
    const nodes = sortNodes(e.source.filter((n) => expiryOf(n) === null || expiryOf(n)! > nowSec));
    return (
      <div className={styles.nodes}>
        <NodeGroup title={`Nodes copiés (${nodes.length})`} nodes={nodes} mark="+" />
      </div>
    );
  }
  const { add, change, remove } = e.diff;
  return (
    <div className={styles.nodes}>
      {add.length > 0 && <NodeGroup title={`Ajoutés (${add.length})`} nodes={sortNodes(add)} mark="+" />}
      {change.length > 0 && (
        <div className={styles.nodeGroup}>
          <span className={styles.nodeGroupTitle}>Modifiés ({change.length}) : avant, puis après</span>
          <ul className={styles.nodeList}>
            {change.map((c) => (
              <li key={`${c.to.key}|${JSON.stringify(c.to.context ?? [])}`}>
                <ul className={styles.nodeList}>
                  {c.from.map((n, i) => <NodeLine key={i} node={n} mark="-" />)}
                  <NodeLine node={c.to} mark="+" />
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
      {remove.length > 0 && <NodeGroup title={`Retirés (${remove.length})`} nodes={sortNodes(remove)} mark="-" />}
    </div>
  );
}

function NodeGroup({ title, nodes, mark }: { title: string; nodes: GroupNodeDTO[]; mark: '+' | '-' }) {
  return (
    <div className={styles.nodeGroup}>
      <span className={styles.nodeGroupTitle}>{title}</span>
      <ul className={styles.nodeList}>
        {nodes.map((n, i) => <NodeLine key={`${i}|${n.key}`} node={n} mark={mark} />)}
      </ul>
    </div>
  );
}

function NodeLine({ node, mark }: { node: GroupNodeDTO; mark: '+' | '-' }) {
  const exp = expiryOf(node);
  return (
    <li className={styles.node}>
      <span className={mark === '+' ? styles.nodeAdd : styles.nodeRemove} aria-label={mark === '+' ? 'ajouté' : 'retiré'}>{mark}</span>
      <span className={styles.nodeKey}>{node.key}</span>
      {!node.value && <span className={`${styles.nodeTag} ${styles.nodeFalse}`}>false</span>}
      {normalizeContext(node.context).map((c) => (
        <span key={`${c.key}=${c.value}`} className={styles.nodeTag} title="Contexte LuckPerms">{c.key}={c.value}</span>
      ))}
      {exp !== null && <span className={styles.nodeTag} title="Permission temporaire">jusqu'au {formatExpiry(exp)}</span>}
      {/\s$/.test(node.key) && <span className={styles.nodeTag} title="Le texte finit par un espace">espace final</span>}
    </li>
  );
}

function ResultRow({ result: r }: { result: GradeCopyResult }) {
  let cls = styles.resultOk;
  let icon = <Check size={14} />;
  let text: string;
  if (!r.ok) {
    cls = styles.resultKo;
    icon = <X size={14} />;
    const doneBefore = r.applied > 1 ? `${r.applied} écritures faites avant l'échec` : r.applied === 1 ? "1 écriture faite avant l'échec" : '';
    const before = [r.created ? 'grade créé' : '', doneBefore].filter(Boolean).join(', ');
    text = `échec, ${r.error ?? 'erreur inconnue'}${before ? ` (${before})` : ''}`;
  } else {
    const done = r.total === 0 ? 'déjà identique, rien à écrire' : plural(r.applied, 'écriture', 'écritures');
    text = `${r.created ? 'créé et copié' : 'copié'} (${done})`;
    if (r.unverified) {
      cls = styles.resultWarn;
      icon = <AlertTriangle size={14} />;
      text += `, ${r.unverified}`;
    }
  }
  return (
    <li className={`${styles.result} ${cls}`}>
      {icon}
      <span><span className={styles.resultName}>{r.name}</span> <span className={styles.resultText}>: {text}</span></span>
    </li>
  );
}
